// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { showNotification } from '@mantine/notifications';
import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { botIdentifier } from '../lib/bot-identifier';
import { ROLES, type Role } from '../utils/permissions';
import {
  getMissingProfileRequirements,
  isOnboardingPending,
  type ProfileMissingRequirement,
} from '../utils/practitioner';
import { useRole } from './useRole';

const REQUIRED_PROFILE_ROLES = new Set<Role>([ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER]);

export interface ProfileCompletionValue {
  loading: boolean;
  isRequired: boolean;
  isComplete: boolean;
  isFinalizing: boolean;
  missing: ProfileMissingRequirement[];
  practitioner: Practitioner | undefined;
  setPractitioner: (p: Practitioner) => void;
}

interface SwapBotResult {
  swapped: boolean;
  practitioner?: Practitioner;
  reason?: string;
}

const ProfileCompletionContext = createContext<ProfileCompletionValue | null>(null);

export function ProfileCompletionProvider({ children }: { children: ReactNode }): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, loading: roleLoading } = useRole();
  const [practitioner, setPractitionerState] = useState<Practitioner | undefined>();
  const [loading, setLoading] = useState(true);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const swapInFlight = useRef(false);

  useEffect(() => {
    if (!profile) {
      setPractitionerState(undefined);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    medplum
      .readResource('Practitioner', profile.id as string)
      .then((p) => {
        if (active) {
          setPractitionerState(p);
        }
      })
      .catch((err) => {
        console.error('[ProfileCompletionProvider] Failed to load practitioner:', err);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [medplum, profile]);

  const setPractitioner = useCallback(
    (p: Practitioner) => {
      setPractitionerState(p);

      // If the profile just became complete while still onboarding-pending,
      // synchronously swap the access policy so the UI only unblocks once the
      // server has upgraded privileges.
      const nowComplete = getMissingProfileRequirements(p).length === 0;
      const pending = isOnboardingPending(p);
      if (!pending || !nowComplete || swapInFlight.current || !p.id) {
        return;
      }

      swapInFlight.current = true;
      setIsFinalizing(true);
      (async () => {
        try {
          const result = (await medplum.executeBot(botIdentifier('onboarding-policy-swap'), {
            practitionerId: p.id,
          })) as SwapBotResult | undefined;
          if (result?.practitioner) {
            setPractitionerState(result.practitioner);
          } else {
            const refreshed = await medplum.readResource('Practitioner', p.id as string);
            setPractitionerState(refreshed);
          }
        } catch (err) {
          console.error('[ProfileCompletionProvider] Onboarding policy swap failed:', err);
          showNotification({
            color: 'red',
            title: 'Account setup',
            message: 'Could not finalize your account. Please contact support.',
            autoClose: false,
          });
        } finally {
          swapInFlight.current = false;
          setIsFinalizing(false);
        }
      })();
    },
    [medplum]
  );

  const value = useMemo<ProfileCompletionValue>(() => {
    // RCM does not require profile completion (no clinical fields to onboard)
    if (role === 'clinical-ops-rcm') {
      return {
        // loading reflects useRole resolution only; practitioner-fetch loading is irrelevant when isRequired=false
        loading: roleLoading,
        isRequired: false,
        isComplete: true,
        isFinalizing: false,
        missing: [],
        practitioner,
        setPractitioner,
      };
    }

    const isRequired = !!role && REQUIRED_PROFILE_ROLES.has(role);
    const pending = practitioner ? isOnboardingPending(practitioner) : false;
    const missing = isRequired && practitioner ? getMissingProfileRequirements(practitioner) : [];
    return {
      loading: loading || roleLoading,
      isRequired,
      isComplete: !isRequired || !pending,
      isFinalizing,
      missing,
      practitioner,
      setPractitioner,
    };
  }, [loading, roleLoading, role, practitioner, isFinalizing, setPractitioner]);

  return <ProfileCompletionContext.Provider value={value}>{children}</ProfileCompletionContext.Provider>;
}

export function useProfileCompletion(): ProfileCompletionValue {
  const ctx = useContext(ProfileCompletionContext);
  if (!ctx) {
    throw new Error('useProfileCompletion must be used within a ProfileCompletionProvider');
  }
  return ctx;
}
